import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useTrickle, { reducedMotion } from './useTrickle';
import useViewport from '../../hooks/useViewport';
import { API_BASE_URL } from '../../config/api';
import { BringYourAiModal } from '../BringYourAiCard';
import AccountDoor from '../../auth/AccountDoor'; // 🔐 accounts (09-30)
import {
  ROOM_CHIPS, resolvePicks, buildRooms, cleanLines, searchRooms, facesOf, faceFor, sidesFor,
  hueOf, visitsLabel, shortCount, moodLabel, liveItems, trunc, isPorch, latestLine, isWideLayout,
  canSend, seatFor, sendIdentity, SEAT_REFUSED_RX, liveLines, SEND_CAP, SEND_MAX, taglineFor, gifLabel, lovedIdsFrom,
  shownChips, bannerOf, artFit, cardRatio,
} from '../../utils/exploreStart';

/**
 * 🧭💬 /explore, look C · Group chat (Nina 09-26; A Marquee and B Bento are
 * saved, the look book is linked in utils/exploreStart.js).
 *
 * The page is a chat app. Rooms are threads in a list; a click plays one on the
 * right, the way a chat app opens a conversation (Nina 09-28: "clicking gives
 * you a preview"; until then resting the mouse did), and the three "Start here"
 * rooms keep talking in the list on their own (option 2: picks always talk).
 * The message box is the door; a double-click, Enter or the row's "Go in" also
 * go straight in. Resting the mouse on a row never moves the pane: the row
 * wakes (typing dots, then its next line) and shows "Go in".
 *
 * 📱 09-27, phones: under 768px wide (or a phone on its side) the list is the
 * page, the search sticks to the top, and a tap opens the room full screen as
 * its thread, playing, with the message box at the bottom as the door. The
 * thread is a history entry, so Back, the iOS edge swipe and Android's back
 * button close it. Tablets keep the two panes: a tap plays a room in the pane,
 * and its message box goes in.
 *
 * Rendered by ExploreSpacesPage OUTSIDE the .explore-dark-mode invert wrapper:
 * this page paints its own dark and light palettes (tokens below), so nothing
 * here is inverted except the What's new sheet, which was built for the invert.
 * ?bot=1 never gets here (utils/exploreStart decideLook).
 */

const FONT_LINK_ID = 'explore-chat-fonts';
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&display=swap';
// The list's scroll position, kept when a phone leaves for a room (or the
// full grid) so Back lands on the same rows. Keyed to the list's own history
// entry, and held in memory: a reload or a fresh visit never inherits it (the
// first entry's key is always 'default', so storage would leak across visits).
let savedSpot = null; // { key, y }
const PAGE_BG = { wide: { dark: '#0D0F13', light: '#F6F7F9' }, narrow: { dark: '#12151A', light: '#FFFFFF' } };

const hrefFor = (space) => `/game-step?gameId=${encodeURIComponent(space.id)}`;
// Cmd, Ctrl, Shift and middle clicks keep the browser's own new-tab behavior.
const plainClick = (e) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button != null && e.button !== 0));

const Chev = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M20 20l-4.2-4.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
);
const MoonIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" fill="currentColor" /></svg>
);
const SunIcon = () => (
  <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><circle cx="12" cy="12" r="4.2" fill="currentColor" /><g stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.3 5.3l1.6 1.6M17.1 17.1l1.6 1.6M5.3 18.7l1.6-1.6M17.1 6.9l1.6-1.6" /></g></svg>
);
const BackIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const PlusIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" /></svg>
);
// 09-29 (Nina's /explore batch): the cards say "Discover", so it never reads
// as a second "+" (Create keeps the only one). 09-30: Your AI wears the 🔌 emoji (Nina), not an icon.
const CardsIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 3.5H17a2.5 2.5 0 0 1 2.5 2.5v11" /><rect x="4.5" y="7" width="11.5" height="14" rx="2.5" /></svg>
);
const HeartIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-7.5-10.3A4.2 4.2 0 0 1 12 7.6a4.2 4.2 0 0 1 7.5 2.6c0 5.7-7.5 10.3-7.5 10.3z" fill="currentColor" /></svg>
);
const FilmIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M7.5 5v14M16.5 5v14M3 9.7h4.5M3 14.3h4.5M16.5 9.7H21M16.5 14.3H21" /></svg>
);
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M8 5l11 7-11 7z" fill="currentColor" /></svg>
);
const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 12l16-8-6 16-2.5-6.5L4 12z" fill="currentColor" /></svg>
);
const Dots = () => <span className="xc-dots" aria-label="typing"><i /><i /><i /></span>;

// Option 2: the three Start here rows take turns saying their next line, with
// a beat of typing dots first. Paused while the tab is hidden.
function usePickTicker(picks, linesById, active) {
  const [typing, setTyping] = useState(null);
  const [shown, setShown] = useState({});
  const key = picks.map(p => `${p.space.id}:${(linesById[p.space.id] || []).length}`).join(',');
  useEffect(() => {
    if (!active || reducedMotion()) return undefined;
    const talkers = picks
      .map(p => ({ id: p.space.id, n: (linesById[p.space.id] || []).length }))
      .filter(x => x.n >= 2);
    if (!talkers.length) return undefined;
    let alive = true;
    let t = null;
    let turn = 0;
    const at = {};
    const tick = () => {
      const who = talkers[turn % talkers.length];
      turn += 1;
      setTyping(who.id);
      t = setTimeout(() => {
        if (!alive) return;
        at[who.id] = ((at[who.id] == null ? -1 : at[who.id]) + 1) % who.n;
        const i = at[who.id];
        setTyping(null);
        setShown(prev => ({ ...prev, [who.id]: i }));
        t = setTimeout(tick, 2300);
      }, 1300);
    };
    t = setTimeout(tick, 1600);
    return () => { alive = false; clearTimeout(t); setTyping(null); };
  }, [active, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { typing, shown };
}

// Resting the mouse on a row wakes it (09-28): typing dots, then its next line,
// on a loop while the mouse stays, starting from the room's newest few lines.
// The pane never moves on hover; a click does that.
function useRowWake(id, lines, active) {
  const [wake, setWake] = useState(null); // { id, typing, line }
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const n = (lines || []).length;
  useEffect(() => {
    setWake(null);
    if (!id || !active || n < 2 || reducedMotion()) return undefined;
    let alive = true;
    let t = null;
    let i = Math.max(0, n - 4);
    const next = () => {
      if (!alive) return;
      setWake({ id, typing: true, line: null });
      t = setTimeout(() => {
        if (!alive) return;
        const now = linesRef.current || [];
        setWake({ id, typing: false, line: now.length ? now[i % now.length] : null });
        i += 1;
        t = setTimeout(next, 2200);
      }, 700);
    };
    t = setTimeout(next, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [id, active, n]);
  return wake && wake.id === id ? wake : null;
}

function Avatar({ space }) {
  const faces = facesOf(space, 2);
  if (faces.length) {
    return (
      <span className={`xc-av${faces.length === 1 ? ' solo' : ''}`} aria-hidden="true">
        {faces.map(f => <img key={f.url} src={f.url} alt="" loading="lazy" decoding="async" />)}
      </span>
    );
  }
  if (space.banner_image_url) {
    return <span className="xc-av one" aria-hidden="true"><img src={space.banner_image_url} alt="" loading="lazy" decoding="async" /></span>;
  }
  return <span className="xc-av none" aria-hidden="true" style={{ '--h': hueOf(space.name) }}>{String(space.name || '?').trim().charAt(0).toUpperCase()}</span>;
}

// 🎞️ A GIF line in a row: the film mark and its short title (or "GIF"). The
// list never plays 50 GIFs; the pane does.
const RowLine = ({ line }) => (line.gif && !line.m
  ? <><b>{line.s}:</b> <span className="xc-gifl"><FilmIcon />{gifLabel(line)}</span></>
  : <><b>{line.s}:</b> {line.m}</>);

// A bubble's inside: the name, the words, and a GIF (the small preview, lazy,
// 160x120 at most) when the line carries one.
const BubbleBody = ({ l, name }) => (
  <>
    <b>{name}</b>
    {l.m ? <span>{l.m}</span> : null}
    {l.gif ? <img className="xc-gif" src={l.gif} alt={l.gifTitle || 'GIF'} loading="lazy" decoding="async" /> : null}
  </>
);

// `go` (two panes only): the row's own "Go in", shown while the mouse rests on
// the row. A sibling of the row's link, never inside it (no link in a link).
// `live` (Live now rows, 09-29): a red LIVE mark by the name, and the film's
// thumbnail as the picture when a watch party has one.
function RoomRow({ space, line, text, typing, on, here, visits, slot, rowProps, go, live = null }) {
  // The pointer hands to the wrapper, so moving onto Go in keeps the row awake.
  const { onPointerEnter, onPointerLeave, ...linkProps } = rowProps || {};
  return (
    <div className="xc-rowwrap" onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave}>
      <a
        className={`xc-row${on ? ' on' : ''}`}
        href={hrefFor(space)}
        aria-current={on ? 'true' : undefined}
        data-room={space.id}
        data-ph-capture-attribute-explore-look="chat"
        data-ph-capture-attribute-explore-slot={slot}
        {...linkProps}
      >
        {live && live.thumb ? (
          <span className="xc-thumb" aria-hidden="true">
            <img src={live.thumb} alt="" loading="lazy" decoding="async" />
            {live.kind === 'party' && <span className="xc-thumb-play"><PlayIcon /></span>}
          </span>
        ) : <Avatar space={space} />}
        <span className="xc-rt">
          {live ? (
            <span className="xc-rnrow"><span className="xc-rn">{space.name}</span><span className="xc-livechip">LIVE</span></span>
          ) : <span className="xc-rn">{space.name}</span>}
          <span className="xc-rl">
            {typing ? <Dots /> : line ? <RowLine line={line} /> : <em>{text || taglineFor(space) || 'Quiet right now'}</em>}
          </span>
        </span>
        <span className="xc-rm">
          {here > 0 && <i className="xc-here" title={`${here} here now`} aria-hidden="true" />}
          {here > 0 && <span className="xc-sr">{here} here now.</span>}
          <span title={visitsLabel(visits)}>{shortCount(visits)}</span>
        </span>
      </a>
      {go && (
        <a
          className="xc-go"
          href={hrefFor(space)}
          onClick={go}
          tabIndex={-1}
          aria-label={`Go into ${space.name}`}
          data-ph-capture-attribute-explore-look="chat"
          data-ph-capture-attribute-explore-slot="go"
        >
          Go in <Chev />
        </a>
      )}
    </div>
  );
}

// 💬 09-28 Send (Nina: "instead of the button saying Drop in, it can be a send
// button, and people can actually do a short ghost chat without entering the
// space"). A line goes to the room's ghost chat as the room page's own
// spectator (same id: the same person once inside), or as this device's seat
// there when it has one (🪪 09-30, exploreStart sendIdentity). From then on the pane
// shows the room's REAL ghost lines, polled (never a socket: that would count
// as someone in the room), with typing dots until the crowd answers.
const POLL_FAST = 3000; // for a minute and a half after a send
const POLL_SLOW = 12000; // after that, while the pane stays open
const FAST_FOR = 90000;
const QUIET_AFTER = 20000; // no answer by then: say so, and offer the door
const SENT_KEY = 'exploreSent'; // sessionStorage: lines sent per room this visit
const NOTES = {
  quiet: 'Quiet in there right now. Go in and say hi?',
  slow: 'Slow down a sec, then try again.',
  error: 'That didn’t send. Try again?',
};

function useSendChat(space, send, active) {
  const [live, setLive] = useState(() => !!(send && send.count > 0));
  const [lines, setLines] = useState([]); // the room's real ghost lines, oldest first
  const [pending, setPending] = useState([]); // sent from here, not back from the room yet
  const [waiting, setWaiting] = useState(false);
  const [note, setNote] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const linesRef = useRef([]);
  const seenAtSend = useRef(null); // the room's line ids when the last line went out
  const lastSendAt = useRef(0);
  const who = useRef(null);
  const host = space.moderator ? space.moderator.name : null;
  const pull = useCallback(async () => {
    let me = who.current ? who.current.me : null;
    if (!me) {
      try {
        const held = seatFor(window.localStorage, space.id);
        me = held ? held.seat : window.localStorage.getItem(`spectator_id_${space.id}`);
      } catch (e) { me = null; }
    }
    const res = await fetch(`${API_BASE_URL}/chat-game/messages?game_id=${encodeURIComponent(space.id)}&type=spectating&limit=12`);
    if (!res.ok) throw new Error(`messages ${res.status}`);
    const got = liveLines((await res.json()).messages, { me, host });
    linesRef.current = got;
    setLines(got);
    setPending(p => p.filter(x => !got.some(g => g.mine && g.m === x.m)));
    if (seenAtSend.current && got.some(g => !g.mine && !seenAtSend.current.has(g.id))) {
      seenAtSend.current = null;
      setWaiting(false);
    }
    return got;
  }, [space.id, host]);

  useEffect(() => {
    if (!live || !active) return undefined;
    let alive = true;
    let t = null;
    let fails = 0;
    const tick = async () => {
      try { await pull(); fails = 0; } catch (e) { fails += 1; }
      if (!alive || fails >= 3) return;
      t = setTimeout(tick, Date.now() - lastSendAt.current < FAST_FOR ? POLL_FAST : POLL_SLOW);
    };
    tick();
    return () => { alive = false; clearTimeout(t); };
  }, [live, active, pull]);

  useEffect(() => {
    if (!waiting) return undefined;
    const t = setTimeout(() => { seenAtSend.current = null; setWaiting(false); setNote('quiet'); }, QUIET_AFTER);
    return () => clearTimeout(t);
  }, [waiting]);

  // Focus = intent: fetch the real lines now, so a send swaps the replay for them at once.
  const warm = () => { if (send && !linesRef.current.length) pull().catch(() => {}); };
  const submit = async (e) => {
    if (e) e.preventDefault();
    const text = draft.replace(/\s+/g, ' ').trim().slice(0, SEND_MAX);
    if (!send || !text || busy || send.count >= SEND_CAP) return;
    let ls = null;
    try { ls = window.localStorage; } catch (err) { ls = null; }
    if (!who.current) who.current = sendIdentity(ls, space.id);
    const { name } = who.current;
    const localId = `local-${Date.now()}`;
    setBusy(true);
    setNote(null);
    setDraft('');
    setPending(p => [...p, { id: localId, s: name, m: text, mine: true }]);
    setLive(true);
    try {
      if (!linesRef.current.length) await pull().catch(() => []);
      seenAtSend.current = new Set(linesRef.current.map(l => l.id));
      lastSendAt.current = Date.now();
      const post = (id) => fetch(`${API_BASE_URL}/chat-game/spectating-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_id: space.id, sender_id: id.me, message: text, from: 'explore',
          ...(id.secret ? { player_secret: id.secret } : { sender_name: id.name }),
        }),
      });
      let res = await post(who.current);
      if (res.status === 400 && who.current.secret) {
        // a seat this device no longer holds: speak as the room's spectator
        const why = await res.json().catch(() => null);
        if (why && SEAT_REFUSED_RX.test(String(why.error || ''))) {
          who.current = sendIdentity(ls, space.id, { asSeat: false });
          res = await post(who.current);
        }
      }
      if (!res.ok) throw Object.assign(new Error('send failed'), { status: res.status });
      // 🪪 09-30: the name the line went out under (a seat's own, or a stand-in
      // when someone in the room already has this device's name)
      const used = await res.json().then(o => (o && typeof o.sender_name === 'string' ? o.sender_name.trim() : ''), () => '');
      if (used) who.current = { ...who.current, name: used };
      const label = who.current.name;
      setPending(p => p.map(x => (x.id === localId && x.s !== label ? { ...x, s: label } : x)));
      send.onSent();
      setWaiting(true);
    } catch (err) {
      seenAtSend.current = null;
      setPending(p => p.filter(x => x.id !== localId));
      setDraft(text);
      setNote(err && err.status === 429 ? 'slow' : 'error');
    } finally {
      setBusy(false);
    }
  };
  return { live, lines, pending, waiting, note, draft, setDraft, busy, submit, warm };
}

// 🖼️ 09-30 (Nina: banners "need to look good" in dark and light, and crisp):
// the picture is measured once it loads. 'wide' art big enough for the strip
// fills it edge to edge; 'soft' art (a 512px square, or anything small) sits
// whole at the right edge, sharp, melting into its own colors blurred across
// the strip, so no banner is stretched past its pixels (utils/exploreStart artFit).
// 🌤️ Light mode (09-30, Nina: "I don't love how these look in light mode") puts
// no words on the picture: on two panes it is a card beside the words, in its
// own shape (`--ar`, from cardRatio); in a phone's thread it sits on top with
// the words under it. Dark mode keeps the picture across the whole strip.
function useBanner(space) {
  const art = useMemo(() => bannerOf(space), [space]);
  const ref = useRef(null);
  // fit is null until measured (the picture waits unseen); an /explore banner's is known (no measuring a srcset)
  const [shape, setShape] = useState(() => ({ fit: (art && art.fit) || null, ar: (art && art.ar) || null }));
  const measure = (img) => {
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    setShape({ fit: (art && art.fit) || artFit(w, h), ar: w / h }); // a ratio is the same in any pixels
  };
  useEffect(() => { if (ref.current && ref.current.complete) measure(ref.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { art: shape.fit === 'none' ? null : art, ref, ...shape, measure, fail: () => setShape({ fit: 'none', ar: null }) };
}

function Banner({ b }) {
  const { art, ref, fit, measure, fail } = b;
  if (!art) return null;
  const soft = fit === 'soft';
  return (
    <div className="xc-pic">
      {soft && <img className="xc-amb" src={art.src} alt="" aria-hidden="true" decoding="async" />}
      <img
        ref={ref}
        className={`xc-art${fit ? ' in' : ''}${soft ? ' xc-art-soft' : ''}`}
        src={art.src}
        srcSet={art.srcSet || undefined}
        sizes={art.sizes || undefined}
        style={art.pos && !soft ? { objectPosition: art.pos } : undefined}
        alt=""
        decoding="async"
        onLoad={(e) => measure(e.currentTarget)}
        onError={fail}
      />
    </div>
  );
}

// `send` = null where Send is off (the message box stays the plain door), else
// { count, onSent } for this room.
function Pane({ space, eyebrow, visits, active, onEnter, send }) {
  const lines = useMemo(() => cleanLines(space), [space]);
  const sides = useMemo(() => sidesFor(lines), [lines]);
  const faces = useMemo(() => facesOf(space, 4), [space]);
  const tag = taglineFor(space) || trunc(space.description, 90);
  const banner = useBanner(space);
  const chat = useSendChat(space, send, active);
  const items = useTrickle(lines, { max: 7, seed: 5, every: [1700, 2800], active: active && !chat.live, resetKey: `${space.id}:${lines.length}` });
  const capped = !!send && send.count >= SEND_CAP;
  const shown = chat.live ? [...chat.lines, ...chat.pending].slice(-8) : null;
  const note = capped ? 'Want to keep talking? Go in.' : (NOTES[chat.note] || null);
  return (
    <div className="xc-pane-in">
      <div className={`xc-head${banner.art ? ' xc-has-pic' : ''}`} style={banner.art && banner.ar ? { '--ar': cardRatio(banner.ar).toFixed(3) } : undefined}>
        <Banner b={banner} />
        <div className="xc-shade" />
        <div className="xc-head-copy">
          {/* 09-30 (Nina): the label ("Most visitors start here") sits beside the name, a small pill; it wraps under a long name */}
          <div className="xc-namerow">
            <h2>{space.name}</h2>
            <span className="xc-eyebrow">{eyebrow}</span>
          </div>
          {/* 09-29: the tagline gets its own line (two at most), so a punchline is never cut on a phone */}
          {tag && <p className="xc-tagline">{tag}</p>}
          <div className="xc-head-meta">
            {faces.length > 0 && (
              <span className="xc-fc" aria-hidden="true">
                {faces.map(f => <img key={f.url} src={f.url} alt="" title={f.name} decoding="async" />)}
              </span>
            )}
            <span className="xc-visits">{visitsLabel(visits)}</span>
          </div>
        </div>
      </div>
      {shown ? (
        <div className="xc-thread" aria-live="polite" aria-label={`Live chat in ${space.name}`}>
          {shown.map((l) => {
            const face = l.mine ? null : faceFor(space, l.s);
            return (
              <div key={l.id} className={`xc-msg ${l.mine ? 'r xc-mine' : 'l'} xc-enter`} style={{ '--h': hueOf(l.s) }}>
                <span className="xc-av1" aria-hidden="true">{face ? <img src={face} alt="" decoding="async" /> : (l.s || 'You').charAt(0).toUpperCase()}</span>
                <div className={`xc-bub${l.gif ? ' xc-bub-media' : ''}`}><BubbleBody l={l} name={l.mine ? (l.s ? `${l.s} (you)` : 'You') : l.s} /></div>
              </div>
            );
          })}
          {chat.waiting && (
            <div className="xc-msg l xc-enter" style={{ '--h': 210 }}>
              <span className="xc-av1" aria-hidden="true" />
              <div className="xc-bub"><Dots /></div>
            </div>
          )}
        </div>
      ) : (
        <div className="xc-thread" aria-label={`Recent lines in ${space.name}`}>
          {items.length ? items.map(({ k, l, leaving, fresh }) => {
            const face = faceFor(space, l.s);
            return (
              <div key={k} className={`xc-msg ${sides[l.s] || 'l'}${fresh ? ' xc-enter' : ''}${leaving ? ' xc-leave' : ''}`} style={{ '--h': hueOf(l.s) }}>
                <span className="xc-av1" aria-hidden="true">{face ? <img src={face} alt="" decoding="async" /> : l.s.charAt(0).toUpperCase()}</span>
                <div className={`xc-bub${l.gif ? ' xc-bub-media' : ''}`}><BubbleBody l={l} name={l.s} /></div>
              </div>
            );
          }) : <div className="xc-quiet">Quiet in here right now. Say hi and get it going.</div>}
        </div>
      )}
      {note && <div className="xc-note" role="status">{note}</div>}
      {send ? (
        <form className="xc-composer xc-composer-send" onSubmit={chat.submit}>
          <input
            className="xc-field"
            value={chat.draft}
            onChange={(e) => chat.setDraft(e.target.value)}
            onFocus={chat.warm}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); e.currentTarget.blur(); } }}
            placeholder={capped ? 'Keep talking inside' : `Say hi to ${space.name}…`}
            aria-label={`Message ${space.name}`}
            maxLength={SEND_MAX}
            enterKeyHint="send"
            autoComplete="off"
            disabled={capped}
          />
          {!capped && (
            <button
              type="submit"
              className="xc-sendbtn"
              disabled={!chat.draft.trim() || chat.busy}
              aria-label="Send"
              title="Send"
              data-ph-capture-attribute-explore-look="chat"
              data-ph-capture-attribute-explore-slot="send"
            >
              <SendIcon />
            </button>
          )}
          <a
            className={`xc-send${capped ? '' : ' xc-send-alt'}`}
            href={hrefFor(space)}
            onClick={onEnter}
            data-ph-capture-attribute-explore-look="chat"
            data-ph-capture-attribute-explore-slot="composer"
          >
            Go in <Chev />
          </a>
        </form>
      ) : (
        <a
          className="xc-composer"
          href={hrefFor(space)}
          onClick={onEnter}
          data-ph-capture-attribute-explore-look="chat"
          data-ph-capture-attribute-explore-slot="composer"
        >
          <span className="xc-input">Say hi to <b>{space.name}</b>…</span>
          <span className="xc-send">Go in <Chev /></span>
        </a>
      )}
    </div>
  );
}

export default function ExploreChat({
  spaces = [], isLoading = false, error = null, onRetry,
  categories = [], totalViews = {}, viewerCounts = {},
  liveStreams = {}, watchParties = {}, explorerStatus = null,
  darkMode = true, onToggleDark, onEnter, onSeeAll, onNavigate,
  ticker = null, whatsNew = null,
  // 09-29: `votes` = this device's space_votes (the page owns them: a deck
  // swipe writes there), feeding the Loved chip. `onDiscover` opens the phone
  // swipe deck (the page mounts it); no prop, no Discover button.
  votes = {}, onDiscover = null,
}) {
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const topRef = useRef(null);
  const backRef = useRef(null);
  const exitTimer = useRef(null);
  const preloaded = useRef(new Set());
  const [query, setQuery] = useState('');
  const [chip, setChip] = useState('all');
  const [paneId, setPaneId] = useState(null);
  const [hoverId, setHoverId] = useState(null); // the row the mouse rests on (it wakes)
  const [byai, setByai] = useState(false); // the Bring your AI sheet
  // 💬 lines sent from this page per room, this visit (the cap counts them)
  const [sent, setSent] = useState(() => {
    try { return JSON.parse(window.sessionStorage.getItem(SENT_KEY)) || {}; } catch (e) { return {}; }
  });
  const [tabVisible, setTabVisible] = useState(() => (typeof document === 'undefined' ? true : !document.hidden));

  // 📱 Two panes or one column: the screen decides, live (a rotation or a
  // resize re-lays the page; it never swaps C for the grid).
  const { width, height } = useViewport();
  const wide = isWideLayout(width, height);
  const location = useLocation();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const [stuck, setStuck] = useState(false);
  const listSpot = useRef({ y: 0, key: null }); // where the list was when a thread opened
  const opener = useRef(null);
  const [restoreTo] = useState(() => (savedSpot && savedSpot.key === location.key ? savedSpot.y : null));

  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const onVis = () => setTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // "/" and Cmd/Ctrl+K find a room, as the search box promises.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const typingHere = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      // 09-29: an open sheet (Bring your AI) keeps the keyboard; the search behind it waits
      if (t && t.closest && t.closest('[aria-modal="true"]')) return;
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typingHere)) {
        e.preventDefault();
        if (searchRef.current) searchRef.current.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => () => clearTimeout(exitTimer.current), []);

  const picks = useMemo(() => resolvePicks(spaces), [spaces]);
  const pickIds = useMemo(() => new Set(picks.map(p => p.space.id)), [picks]);
  const rooms = useMemo(() => buildRooms(spaces, { categories, exclude: pickIds }), [spaces, categories, pickIds]);
  const linesById = useMemo(() => {
    const m = {};
    for (const s of spaces) if (s && s.id) m[s.id] = cleanLines(s);
    return m;
  }, [spaces]);
  const live = useMemo(() => liveItems({ spaces, liveStreams, watchParties, viewerCounts, explorerStatus }), [spaces, liveStreams, watchParties, viewerCounts, explorerStatus]);
  // A room shows once: while it is live it sits under Live now, not again below.
  const liveIds = useMemo(() => new Set(live.filter(it => it.space).map(it => it.space.id)), [live]);
  const shownPicks = useMemo(() => picks.filter(p => !liveIds.has(p.space.id)), [picks, liveIds]);
  const publicCount = useMemo(() => spaces.filter(s => s && !isPorch(s)).length, [spaces]);
  const q = query.trim();
  const results = useMemo(() => (q ? searchRooms(spaces, q) : []), [spaces, q]);
  // ❤️ Loved (09-29): every room this device liked, in the list's order, picks
  // included (it is the viewer's own list). Live rooms stay under Live now.
  const lovedRooms = useMemo(() => {
    const ids = lovedIdsFrom(votes);
    return ids.size ? spaces.filter(s => s && s.id && ids.has(s.id) && !isPorch(s)).map(s => ({ space: s, cats: new Set() })) : [];
  }, [spaces, votes]);
  // 🧱 09-29: Learn and Life (purpose chips) show only while a room below would
  // sit under them; a chip that goes away hands the list back to All.
  const chips = useMemo(() => shownChips(rooms.filter(r => !liveIds.has(r.space.id))), [rooms, liveIds]);
  const chipOn = chip === 'loved' || chips.some(c => c.key === chip) ? chip : 'all';
  // ...for good: when the chip comes back (its room's party over), the list
  // stays where the viewer is instead of jumping back to it on its own.
  useEffect(() => { if (chip !== chipOn) setChip(chipOn); }, [chip, chipOn]);
  const lovedOn = chipOn === 'loved' && lovedRooms.length > 0;
  const shownRooms = (lovedOn ? lovedRooms : (chipOn === 'all' || chipOn === 'loved') ? rooms : rooms.filter(r => r.cats.has(chipOn))).filter(r => !liveIds.has(r.space.id));

  const byId = useMemo(() => new Map(spaces.filter(s => s && s.id).map(s => [s.id, s])), [spaces]);
  // 📱 The open thread (one column only): its room rides this history entry.
  const threadId = !wide && location.state && location.state.xcRoom ? location.state.xcRoom : null;
  const thread = threadId && byId.get(threadId) && !isPorch(byId.get(threadId)) ? byId.get(threadId) : null;
  const ticker2 = usePickTicker(shownPicks, linesById, tabVisible && !thread);
  const wake = useRowWake(wide ? hoverId : null, hoverId ? linesById[hoverId] : null, tabVisible && !thread);
  // What a row says: a woken row's dots or next line win over the caller's.
  const say = (id, line, typing = false) => (wake && wake.id === id
    ? { line: wake.line || line, typing: wake.typing }
    : { line, typing });
  const pane = (paneId && byId.get(paneId) && !isPorch(byId.get(paneId)) ? byId.get(paneId) : null) || (picks[0] && picks[0].space) || null;
  const eyebrowFor = (space) => {
    const pickReason = (picks.find(p => p.space.id === space.id) || {}).reason;
    // the room's own chip (09-29: its purpose's first; with none, the first it sits under, as before)
    const roomChip = (rooms.find(r => r.space.id === space.id) || {}).chip;
    const catLabel = roomChip ? (ROOM_CHIPS.find(c => c.key === roomChip) || {}).label : null;
    return pickReason || moodLabel(space.space_mood) || catLabel || 'A room';
  };

  const views = (s) => totalViews[s.id] || s.total_views || 0;
  const samePlace = { pathname: location.pathname, search: location.search, hash: location.hash };
  // Where the list was, so Back from a room (or the grid) lands on the same rows.
  const rememberList = () => {
    if (wide) return;
    savedSpot = thread ? { ...listSpot.current } : { key: location.key, y: window.scrollY || 0 };
  };
  // `replace` from a thread: Back from the room lands on the list, not the preview.
  const enter = (space, opts) => (e) => {
    if (!plainClick(e)) return;
    e.preventDefault();
    rememberList();
    if (onEnter) onEnter(space, opts);
  };
  const openThread = (space) => {
    clearTimeout(exitTimer.current);
    listSpot.current = { key: location.key, y: window.scrollY || 0 };
    opener.current = space.id;
    navigate(samePlace, { state: { ...(location.state || {}), xcRoom: space.id } });
  };
  // The ‹ button slides the thread away, then steps back. Back, the edge swipe
  // and Android's back close it at once (iOS already animated its own swipe).
  const closeThread = () => {
    if (leaving) return;
    const back = () => {
      if (window.history.length > 1) navigate(-1);
      else navigate(samePlace, { replace: true, state: null });
    };
    if (reducedMotion()) { back(); return; }
    setLeaving(true);
    exitTimer.current = setTimeout(back, 190);
  };
  const goIn = (space) => { rememberList(); if (onEnter) onEnter(space); };
  // The pictures a click is about to need, fetched while the mouse rests.
  const preload = (space) => {
    if (!space || preloaded.current.has(space.id)) return;
    preloaded.current.add(space.id);
    const art = bannerOf(space);
    if (art) { // the same candidate the pane will pick (srcset + sizes before src)
      const img = new Image();
      img.decoding = 'async';
      if (art.srcSet) { img.srcset = art.srcSet; img.sizes = art.sizes; }
      img.src = art.src;
    }
    facesOf(space, 4).forEach(({ url }) => {
      if (!url) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    });
  };
  // ↑/↓ walk the rows; the pane follows focus (onFocus below).
  const focusRow = (from, step) => {
    const rows = listRef.current ? Array.from(listRef.current.querySelectorAll('.xc-row[data-room]')) : [];
    const to = rows[rows.indexOf(from) + step];
    if (to) to.focus();
    return !!to;
  };
  // One column: a tap opens the room as its thread. Two panes (mouse or
  // finger): a click plays the room in the pane; a double-click, Enter or the
  // row's Go in goes in.
  const onRowClick = (space) => (e) => {
    if (!plainClick(e)) return;
    e.preventDefault();
    if (!wide) { openThread(space); return; }
    setPaneId(space.id);
  };
  const onRowKey = (space) => (e) => {
    if (!wide) return;
    if (e.key === 'Enter' && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
      e.preventDefault(); // a link's own Enter is a click, which only plays the room
      goIn(space);
    } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && focusRow(e.currentTarget, e.key === 'ArrowDown' ? 1 : -1)) {
      e.preventDefault();
    }
  };
  const rowProps = (space) => ({
    onClick: onRowClick(space),
    onDoubleClick: (e) => { if (wide && plainClick(e)) { e.preventDefault(); goIn(space); } },
    onKeyDown: onRowKey(space),
    onPointerEnter: (e) => { if (wide && e.pointerType !== 'touch') { setHoverId(space.id); preload(space); } },
    onPointerLeave: () => setHoverId(id => (id === space.id ? null : id)),
    onFocus: () => { if (wide) setPaneId(space.id); },
  });
  const goProps = (space) => (wide ? enter(space) : null);
  const sendFor = (space) => (canSend(space) ? {
    count: sent[space.id] || 0,
    onSent: () => setSent((prev) => {
      const next = { ...prev, [space.id]: (prev[space.id] || 0) + 1 };
      try { window.sessionStorage.setItem(SENT_KEY, JSON.stringify(next)); } catch (e) { /* private mode */ }
      return next;
    }),
  } : null);
  const go = (path) => (e) => {
    if (!plainClick(e)) return;
    e.preventDefault();
    rememberList();
    if (onNavigate) onNavigate(path);
  };
  const lastLine = (id) => latestLine(byId.get(id), linesById[id]);
  const onSearchKey = (e) => {
    if (e.key === 'Escape') { setQuery(''); return; }
    if (e.key === 'ArrowDown' && wide) {
      const first = listRef.current && listRef.current.querySelector('.xc-row[data-room]');
      if (first) { e.preventDefault(); first.focus(); }
      return;
    }
    if (e.key !== 'Enter') return;
    if (!wide) { e.preventDefault(); e.currentTarget.blur(); return; } // the keyboard steps aside for the results
    if (results.length) {
      e.preventDefault();
      const first = listRef.current && listRef.current.querySelector('[data-room]');
      if (first) first.focus();
    }
  };

  // 📱 While a thread is open the list behind it holds still (the swipe decks
  // lock the page the same way).
  useEffect(() => {
    if (!thread) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [!!thread]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus: into the thread's ‹ when it opens, back to its row when it closes.
  const threadKey = thread ? thread.id : null;
  useEffect(() => {
    if (threadKey) {
      if (backRef.current) backRef.current.focus({ preventScroll: true });
      return undefined;
    }
    setLeaving(false);
    const id = opener.current;
    opener.current = null;
    const row = id && listRef.current && listRef.current.querySelector(`[data-room="${id}"]`);
    if (row) row.focus({ preventScroll: true });
    return undefined;
  }, [threadKey]);

  useEffect(() => {
    if (!threadKey) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeThread(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // every render: closeThread reads the newest location

  // A tablet turned from one column to two: the open thread becomes the pane.
  useEffect(() => {
    if (wide && location.state && location.state.xcRoom) setPaneId(location.state.xcRoom);
  }, [wide]); // eslint-disable-line react-hooks/exhaustive-deps

  // Back from a room: the list returns to where it was, once its rows exist.
  useEffect(() => {
    if (restoreTo == null || wide || !spaces.length || savedSpot === null) return;
    savedSpot = null;
    window.scrollTo(0, restoreTo);
  }, [restoreTo, wide, spaces.length]);

  // The search bar gets its hairline only once it is stuck to the top.
  useEffect(() => {
    if (wide || typeof IntersectionObserver === 'undefined' || !topRef.current) return undefined;
    const io = new IntersectionObserver(([en]) => setStuck(!en.isIntersecting));
    io.observe(topRef.current);
    return () => io.disconnect();
  }, [wide]);

  // The phone's status bar takes the page's color (index.html says black).
  const pageBg = PAGE_BG[wide ? 'wide' : 'narrow'][darkMode ? 'dark' : 'light'];
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return undefined;
    const prev = meta.getAttribute('content');
    meta.setAttribute('content', pageBg);
    return () => { if (prev != null) meta.setAttribute('content', prev); };
  }, [pageBg]);

  const loading = isLoading && !spaces.length;
  const failed = !!error && !spaces.length;

  return (
    <div className="xc" data-mode={darkMode ? 'dark' : 'light'} data-layout={wide ? 'wide' : 'narrow'}>
      <style>{CSS}</style>
      <style>{`html,body{background:${pageBg};}`}</style>
      {ticker && <div className="xc-ticker">{ticker}</div>}
      <header className="xc-top" ref={topRef}>
        <h1 className="xc-wm"><span className="xc-wm-n">myspa<i>i</i>ce</span><span className="xc-wm-t">together, al<i>i</i>ve.</span></h1>
        <span className="xc-grow" />
        {whatsNew && <div className="xc-new">{whatsNew}</div>}
        {/* 09-29 (Nina): "Bring your AI" takes AI Marketplace's place (its sheet
            keeps a Browse AIs door), and Create a space becomes a "+".
            09-30 (Nina): "🔌 Your AI" is the one filled button (bring your AI
            first); the "+" drops its green and shrinks to a plain outlined
            icon; the light/dark switch is a smaller bare icon. Phones keep
            both lower down. */}
        {wide && (
          <>
            <button type="button" className="xc-byai" onClick={() => setByai(true)} aria-haspopup="dialog" aria-label="Bring your AI" data-ph-capture-attribute-explore-look="chat" data-ph-capture-attribute-explore-slot="byai">
              <span className="xc-emo" aria-hidden="true">🔌</span>Your AI<span className="xc-tip" aria-hidden="true">Bring your AI in</span>
            </button>
            <a className="xc-plus" href="/custom-space" onClick={go('/custom-space')} aria-label="Create a space" data-ph-capture-attribute-explore-look="chat" data-ph-capture-attribute-explore-slot="create">
              <PlusIcon size={14} /><span className="xc-tip" aria-hidden="true">Create a space</span>
            </a>
          </>
        )}
        {/* 🔐 accounts (09-30, Nina's pick C): a person icon, its name on hover like the +.
            Renders nothing while sign-in is off. Wide and phone both: it sits by the mode switch. */}
        <AccountDoor />
        <button type="button" className="xc-mode" onClick={onToggleDark} aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'} title={darkMode ? 'Light mode' : 'Dark mode'}>
          {darkMode ? <SunIcon /> : <MoonIcon />}
        </button>
      </header>

      <div className="xc-app">
        <aside className="xc-list" ref={listRef} aria-label="Rooms">
          <div className={`xc-find${stuck ? ' stuck' : ''}`}>
            <label className="xc-search">
              <SearchIcon />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
                placeholder={!wide ? 'Search' : loading ? 'Search rooms' : `Search ${publicCount} rooms`}
                aria-label="Search rooms"
                enterKeyHint="search"
                spellCheck={false}
                autoComplete="off"
              />
              {query ? (
                <button type="button" className="xc-clear" onClick={() => { setQuery(''); if (searchRef.current) searchRef.current.focus(); }} aria-label="Clear search">✕</button>
              ) : wide && <kbd>/</kbd>}
            </label>
            {!wide && onDiscover && (
              <button type="button" className="xc-disc" onClick={onDiscover} aria-label="Discover rooms" title="Discover rooms" data-ph-capture-attribute-explore-look="chat" data-ph-capture-attribute-explore-slot="discover">
                <CardsIcon /><span>Discover</span>
              </button>
            )}
            {!wide && (
              <a className="xc-compose" href="/custom-space" onClick={go('/custom-space')} title="Create a space" aria-label="Create a space" data-ph-capture-attribute-explore-look="chat" data-ph-capture-attribute-explore-slot="create">
                <PlusIcon size={19} />
              </a>
            )}
          </div>

          {!wide && failed && (
            <div className="xc-fail xc-fail-list">
              <p>Couldn’t load the rooms.</p>
              {onRetry && <button type="button" className="xc-create" onClick={onRetry}>Try again</button>}
            </div>
          )}

          {loading && (
            <div aria-busy="true" aria-label="Loading rooms">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="xc-row xc-skel-row"><span className="xc-skel xc-skel-av" /><span className="xc-rt"><span className="xc-skel xc-skel-a" /><span className="xc-skel xc-skel-b" /></span><span /></div>
              ))}
            </div>
          )}

          {!loading && q && (
            <>
              <div className="xc-sec">{results.length ? `${results.length} room${results.length === 1 ? '' : 's'}` : 'No rooms match'}</div>
              {results.map(s => (
                <RoomRow key={s.id} space={s} {...say(s.id, lastLine(s.id))} on={wide && !!pane && pane.id === s.id} here={viewerCounts[s.id] || 0} visits={views(s)} slot="search" rowProps={rowProps(s)} go={goProps(s)} />
              ))}
              {!results.length && (
                <p className="xc-empty">Nothing called “{q}” yet. <a href="/custom-space" onClick={go('/custom-space')}>Build it</a></p>
              )}
            </>
          )}

          {!loading && !q && (
            <>
              {live.length > 0 && (
                <>
                  <div className="xc-sec xc-sec-live"><i className="xc-live-dot" aria-hidden="true" />Live now</div>
                  {live.map(it => (it.kind === 'explorer' ? (
                    <a key={it.key} className="xc-row" href="/explorer/theater" onClick={go('/explorer/theater')} data-ph-capture-attribute-explore-look="chat" data-ph-capture-attribute-explore-slot="live">
                      <span className="xc-av one" aria-hidden="true">{it.avatar ? <img src={it.avatar} alt="" decoding="async" /> : <span className="xc-live-mark" />}</span>
                      <span className="xc-rt"><span className="xc-rn">{it.title}</span><span className="xc-rl">{it.line}</span></span>
                      <span className="xc-rm"><i className="xc-live-dot" aria-hidden="true" /></span>
                    </a>
                  ) : (
                    <RoomRow key={it.key} space={it.space} text={it.line} live={it} on={wide && !!pane && pane.id === it.space.id} here={viewerCounts[it.space.id] || 0} visits={views(it.space)} slot="live" rowProps={rowProps(it.space)} go={goProps(it.space)} />
                  )))}
                </>
              )}

              {shownPicks.length > 0 && <div className="xc-sec">Start here</div>}
              {shownPicks.map(p => {
                const i = ticker2.shown[p.space.id];
                const lines = linesById[p.space.id] || [];
                return (
                  <RoomRow
                    key={p.space.id}
                    space={p.space}
                    {...say(p.space.id, i != null && lines[i] ? lines[i] : lastLine(p.space.id), ticker2.typing === p.space.id)}
                    on={wide && !!pane && pane.id === p.space.id}
                    here={viewerCounts[p.space.id] || 0}
                    visits={views(p.space)}
                    slot="pick"
                    rowProps={rowProps(p.space)}
                    go={goProps(p.space)}
                  />
                );
              })}

              {rooms.length > 0 && (
                <div className="xc-chips" role="group" aria-label="Show rooms">
                  {chips.map(c => (
                    <button key={c.key} type="button" className="xc-chip" aria-pressed={chipOn === c.key || (c.key === 'all' && chipOn === 'loved' && !lovedOn)} onClick={() => setChip(c.key)}>{c.label}</button>
                  ))}
                  {lovedRooms.length > 0 && (
                    <button type="button" className="xc-chip xc-chip-loved" aria-pressed={lovedOn} onClick={() => setChip('loved')} title="Rooms you loved, on this device">
                      <HeartIcon />Loved <span className="xc-ct">{lovedRooms.length}</span>
                    </button>
                  )}
                </div>
              )}
              {rooms.length > 0 && <div className="xc-sec">{lovedOn ? 'Loved' : 'More rooms'}</div>}
              {shownRooms.map(r => (
                <RoomRow key={r.space.id} space={r.space} {...say(r.space.id, lastLine(r.space.id))} on={wide && !!pane && pane.id === r.space.id} here={viewerCounts[r.space.id] || 0} visits={views(r.space)} slot={lovedOn ? 'loved' : 'room'} rowProps={rowProps(r.space)} go={goProps(r.space)} />
              ))}
            </>
          )}

          {!loading && spaces.length > 0 && (
            <a
              className="xc-every"
              href="/explore?view=all"
              onClick={(e) => { if (!plainClick(e)) return; e.preventDefault(); if (onSeeAll) onSeeAll({}); }}
              data-ph-capture-attribute-explore-look="chat"
              data-ph-capture-attribute-explore-slot="all"
            >
              See every space <span>{spaces.length}</span>
            </a>
          )}
          {!wide && !loading && (
            <div className="xc-foot">
              <button type="button" className="xc-byai-foot" onClick={() => setByai(true)} aria-haspopup="dialog" aria-label="Bring your AI" data-ph-capture-attribute-explore-look="chat" data-ph-capture-attribute-explore-slot="byai">
                <span className="xc-emo" aria-hidden="true">🔌</span>Your AI
              </button>
            </div>
          )}
          {/* 09-29: the app linked neither page before (spec §7) */}
          <nav className="xc-legal" aria-label="Legal">
            <a href="/terms.html">Terms</a><span aria-hidden="true">·</span><a href="/privacy.html">Privacy</a>
          </nav>
        </aside>

        {wide && (
          <main className="xc-pane">
            {pane ? (
              <Pane key={pane.id} space={pane} eyebrow={eyebrowFor(pane)} visits={views(pane)} active={tabVisible} onEnter={enter(pane)} send={sendFor(pane)} />
            ) : failed ? (
              <div className="xc-fail">
                <p>Couldn’t load the rooms.</p>
                {onRetry && <button type="button" className="xc-create" onClick={onRetry}>Try again</button>}
              </div>
            ) : (
              <div className="xc-pane-in" aria-hidden="true">
                <div className="xc-head xc-skel" />
                <div className="xc-thread" />
                <div className="xc-composer xc-composer-off"><span className="xc-input">Loading rooms…</span></div>
              </div>
            )}
          </main>
        )}
      </div>

      {/* 📱 the thread: one room, full screen, playing; the message box is the door */}
      {thread && (
        <div className={`xc-sheet${leaving ? ' xc-sheet-out' : ''}`} role="dialog" aria-modal="true" aria-label={thread.name}>
          <button type="button" className="xc-back" ref={backRef} onClick={closeThread} aria-label="Back to the rooms">
            <BackIcon />
          </button>
          <Pane key={thread.id} space={thread} eyebrow={eyebrowFor(thread)} visits={views(thread)} active={tabVisible} onEnter={enter(thread, { replace: true })} send={sendFor(thread)} />
        </div>
      )}

      {/* 🔌 Both doors in one sheet (09-29): your own AI joins as itself, or
          make one here and we voice it; Browse AIs keeps the marketplace's
          door. On a phone the make-one door leads (no Claude Code there). */}
      {byai && (
        <BringYourAiModal onClose={() => setByai(false)} voicedHref="/agents/import" browseHref="/marketplace" voicedFirst={!wide} />
      )}
    </div>
  );
}

const CSS = `
.xc{--bg:#0D0F13;--list:#12151A;--row-hover:#1A1E25;--row-on:#1F242D;--ink:#ECEEF2;--muted:#8B92A0;--line:#262B34;--accent:#4CAF50;--accent-ink:#04120A;--live:92,225,230;--bub-s:24%;--bub-l:17%;--bub-ink:#E7EAEF;--name-l:74%;--name-s:72%;--focus:#5CE1E6;--skel:#1A1E25;
  height:100vh;height:100dvh;min-height:540px;display:flex;flex-direction:column;background:var(--bg);color:var(--ink);font-family:Geist,ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;font-size:15px;line-height:1.45;-webkit-font-smoothing:antialiased;text-align:left;color-scheme:dark;-webkit-tap-highlight-color:transparent}
.xc[data-mode="light"]{--bg:#F6F7F9;--list:#FFFFFF;--row-hover:#F2F4F7;--row-on:#E9EEF6;--ink:#111317;--muted:#5E6573;--line:#E3E6EB;--accent:#2E7D32;--accent-ink:#FFFFFF;--live:10,150,164;--bub-s:62%;--bub-l:94%;--bub-ink:#1B1E24;--name-l:33%;--name-s:60%;--focus:#2E7D32;--skel:#E9ECF0;color-scheme:light}
.xc *,.xc *::before,.xc *::after{box-sizing:border-box}
:where(.xc) img{display:block;max-width:none}
:where(.xc) a{color:inherit;text-decoration:none}
:where(.xc) button{font:inherit;color:inherit;background:none;border:0;cursor:pointer;padding:0}
:where(.xc) h1,:where(.xc) h2,:where(.xc) p{margin:0}
.xc a:focus-visible,.xc button:focus-visible{outline:2px solid var(--focus);outline-offset:2px;border-radius:10px}
.xc-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
.xc-grow{flex:1}
.xc-ticker{flex:none}
.xc-top{flex:none;height:62px;display:flex;align-items:center;gap:6px;padding:0 92px 0 22px;border-bottom:1px solid var(--line);position:relative;z-index:20}
.xc-wm{display:flex;align-items:baseline;gap:12px;line-height:1;white-space:nowrap;font-weight:inherit;font-size:inherit}
.xc-wm-n{font-weight:300;font-size:24px;letter-spacing:-.01em}
.xc-wm i{font-style:normal;font-weight:600;color:#43A047}
.xc-wm-t{font-family:Georgia,"Times New Roman",serif;font-size:13px;letter-spacing:.08em}
.xc-new{position:relative;z-index:30;flex:none;display:flex;margin-right:8px}
/* 09-30 (Nina: What's new "shouldn't be a large button, and should open on
   hover"): a quiet text link with the unread dot; the sheet opens on hover
   (WhatsNewPill openOnHover). Its wrapper reaches down to the sheet, so the
   pointer crosses over without leaving it. */
.xc-new>div{padding-bottom:12px;margin-bottom:-12px}
.xc-new-pill{display:inline-flex;align-items:center;gap:6px;height:30px;padding:0 9px;border-radius:8px;font-size:13px;font-weight:500;color:var(--muted);white-space:nowrap;transition:color .12s,background-color .12s}
.xc-new-pill:hover,.xc-new-pill[aria-expanded="true"]{color:var(--ink);background:var(--row-hover)}
.xc-new-pill>span{width:6px;height:6px;border-radius:50%;background:#FF5C5C}
.xc .xc-new [role="dialog"]{max-height:calc(100vh - 124px);overflow-y:auto!important;overscroll-behavior:contain}
.xc[data-mode="dark"] .xc-new [role="dialog"]{filter:invert(1) hue-rotate(180deg)}
/* 09-30 (Nina: "We want people to be interested in bringing their AI first"):
   🔌 Your AI is the bar's one filled button, in the page's own ink (light on
   dark, dark on light); Create is a plain outlined "+", its name in a
   tooltip; the light/dark switch is smaller still, a bare icon.
   .xc-create stays for the Try again buttons. */
.xc-byai{position:relative;height:32px;display:inline-flex;align-items:center;gap:8px;padding:0 15px 0 6px;margin-right:6px;border-radius:999px;background:var(--ink);color:var(--bg);font-weight:600;font-size:13.5px;white-space:nowrap;flex:none;transition:background-color .12s}
.xc-byai:hover{background:color-mix(in srgb,var(--ink) 86%,var(--bg))}
/* 🔌 sits in a small white circle: the plug is black, and on the dark pill (light mode) it would vanish */
.xc-emo{display:inline-grid;place-items:center;width:21px;height:21px;border-radius:50%;background:#fff;font-size:12.5px;line-height:1;flex:none}
.xc-plus{position:relative;flex:none;display:grid;place-items:center;width:30px;height:30px;border-radius:50%;color:var(--ink);box-shadow:inset 0 0 0 1px var(--line);transition:background-color .12s}
.xc-plus:hover{background:var(--row-hover)}
/* 09-30 (Nina: "make the dark/light mode a smaller button"): a quiet icon with
   no ring, the smallest thing in the bar (phones keep a finger-sized target) */
.xc-mode{position:relative;flex:none;display:grid;place-items:center;width:26px;height:26px;border-radius:50%;color:var(--muted);transition:color .12s,background-color .12s}
.xc-mode:hover{color:var(--ink);background:var(--row-hover)}
.xc-mode svg{width:14px;height:14px}
.xc[data-layout="narrow"] .xc-mode{width:34px;height:34px}
.xc .xc-byai:focus-visible{border-radius:999px}
.xc .xc-plus:focus-visible,.xc .xc-mode:focus-visible{border-radius:50%}
.xc-tip{position:absolute;top:calc(100% + 10px);left:50%;transform:translateX(-50%);padding:6px 10px;border-radius:7px;background:var(--ink);color:var(--bg);font-size:12px;font-weight:600;line-height:1.2;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .12s;z-index:40}
.xc-tip::before{content:'';position:absolute;top:-4px;left:50%;margin-left:-4px;width:8px;height:8px;background:var(--ink);transform:rotate(45deg)}
.xc-plus:hover .xc-tip,.xc-plus:focus-visible .xc-tip,.xc-byai:hover .xc-tip,.xc-byai:focus-visible .xc-tip{opacity:1}
.xc-create{height:34px;display:inline-flex;align-items:center;padding:0 14px;border-radius:9px;box-shadow:inset 0 0 0 1px var(--line);font-weight:600;font-size:13.5px;white-space:nowrap}
.xc-create:hover{background:var(--row-hover)}
.xc-app{flex:1;min-height:0;display:grid;grid-template-columns:clamp(340px,30vw,420px) minmax(0,1fr)}
.xc-list{background:var(--list);border-right:1px solid var(--line);overflow-y:auto;overscroll-behavior:contain;scrollbar-width:thin;scrollbar-color:var(--line) transparent;padding:14px 10px 20px;min-height:0}
.xc-search{display:flex;align-items:center;gap:9px;height:40px;margin:0 6px 14px;padding:0 10px 0 12px;border-radius:10px;background:var(--row-hover);color:var(--muted);cursor:text}
.xc-search input{flex:1;min-width:0;height:100%;border:0;outline:0;background:none;color:var(--ink);font:inherit;font-size:14px;padding:0}
.xc-search input::placeholder{color:var(--muted);opacity:1}
.xc-search:focus-within{box-shadow:0 0 0 2px var(--focus)}
.xc-search kbd{font:500 11px/1 "Geist Mono",ui-monospace,monospace;padding:4px 6px;border-radius:5px;box-shadow:inset 0 0 0 1px var(--line);color:var(--muted)}
.xc-clear{width:22px;height:22px;border-radius:50%;font-size:11px;color:var(--muted)}
.xc-clear:hover{background:var(--line);color:var(--ink)}
.xc-sec{display:flex;align-items:center;gap:8px;font:500 11px/1 "Geist Mono",ui-monospace,monospace;letter-spacing:.09em;text-transform:uppercase;color:var(--muted);padding:6px 12px 8px}
.xc-row{display:grid;grid-template-columns:52px minmax(0,1fr) auto;gap:12px;align-items:center;padding:10px 12px;border-radius:12px;position:relative}
@media (hover:hover){.xc-row:hover,.xc-rowwrap:hover .xc-row{background:var(--row-hover)}.xc-row:hover .xc-av img,.xc-rowwrap:hover .xc-av img{border-color:var(--row-hover)}}
.xc-row.on,.xc-rowwrap .xc-row.on,.xc-row:active{background:var(--row-on)}
.xc-row.on{box-shadow:inset 3px 0 0 var(--accent)} /* the open room, clear of a mere hover */
.xc-av{position:relative;width:52px;height:44px;flex:none}
.xc-av img{position:absolute;width:36px;height:36px;border-radius:50%;object-fit:cover;border:2px solid var(--list);background:var(--row-hover)}
.xc-av img:nth-child(1){left:0;top:0}
.xc-av img:nth-child(2){left:16px;top:8px}
.xc-av.solo img,.xc-av.one img{width:44px;height:44px;left:4px;top:0}
.xc-av.one img{border-radius:12px}
.xc-av.none{display:grid;place-items:center;width:44px;margin-left:4px;border-radius:12px;font-weight:700;background:hsl(var(--h) var(--bub-s) var(--bub-l));color:hsl(var(--h) var(--name-s) var(--name-l))}
.xc-row.on .xc-av img,.xc-row:active .xc-av img{border-color:var(--row-on)}
.xc-rt{display:block;min-width:0}
.xc-rn{display:block;font-weight:600;font-size:14.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.xc-rnrow{display:flex;align-items:center;gap:7px;min-width:0}
.xc-rnrow .xc-rn{min-width:0}
.xc-livechip{flex:none;font:600 9.5px/1 "Geist Mono",ui-monospace,monospace;letter-spacing:.08em;color:#fff;background:#EF4444;padding:3px 5px 2px;border-radius:4px}
.xc-thumb{position:relative;width:52px;height:40px;border-radius:9px;overflow:hidden;background:#111}
.xc-thumb img{width:100%;height:100%;object-fit:cover}
.xc-thumb-play{position:absolute;inset:0;display:grid;place-items:center;color:#fff;filter:drop-shadow(0 1px 2px rgba(0,0,0,.6))}
.xc-gifl{display:inline-flex;align-items:center;gap:4px;color:var(--ink);vertical-align:-2px}
.xc-rl{display:block;margin-top:3px;font-size:13px;color:var(--muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;height:19px}
.xc-rl b{color:var(--ink);font-weight:500}
.xc-rl em{font-style:normal}
.xc-rm{display:flex;flex-direction:column;align-items:flex-end;justify-content:center;gap:7px;min-height:36px;font:400 11.5px/1 "Geist Mono",ui-monospace,monospace;color:var(--muted);font-variant-numeric:tabular-nums}
/* Go in: rests over the row's right edge while the mouse is on the row (or the
   row has keyboard focus: Enter goes in); a fade hides the text beneath it. */
.xc-rowwrap{position:relative}
.xc-rm{transition:opacity .12s}
.xc-go{position:absolute;right:10px;top:50%;transform:translateY(-50%);display:inline-flex;align-items:center;gap:2px;height:30px;padding:0 7px 0 12px;border-radius:9px;background:var(--accent);color:var(--accent-ink);font-weight:700;font-size:13px;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .12s}
.xc-go::before{content:'';position:absolute;right:100%;top:-17px;bottom:-17px;width:30px;background:linear-gradient(90deg,transparent,var(--row-hover));pointer-events:none}
.xc-row.on+.xc-go::before{background:linear-gradient(90deg,transparent,var(--row-on))}
.xc-go:hover{filter:brightness(1.08)}
@media (hover:hover){.xc-rowwrap:hover .xc-go{opacity:1;pointer-events:auto}.xc-rowwrap:hover .xc-rm{opacity:0}}
.xc-rowwrap:has(.xc-row:focus-visible) .xc-go{opacity:1;pointer-events:auto}
.xc-rowwrap:has(.xc-row:focus-visible) .xc-rm{opacity:0}
.xc-here{width:9px;height:9px;border-radius:50%;background:rgb(var(--live));box-shadow:0 0 0 4px rgba(var(--live),.16);animation:xcPulse 2.4s ease-in-out infinite}
.xc-live-dot{display:inline-block;width:8px;height:8px;border-radius:50%;background:#EF4444;box-shadow:0 0 0 4px rgba(239,68,68,.18);animation:xcRed 1.6s ease-in-out infinite}
.xc-sec-live{color:#EF4444}
.xc-live-mark{display:block;width:44px;height:44px;margin-left:4px;border-radius:12px;background:radial-gradient(circle at 50% 50%,#EF4444 0 6px,transparent 7px),var(--row-hover)}
@keyframes xcPulse{50%{box-shadow:0 0 0 7px rgba(var(--live),.04)}}
@keyframes xcRed{50%{box-shadow:0 0 0 7px rgba(239,68,68,.04)}}
.xc-dots{display:inline-flex;gap:3px;align-items:center;height:19px}
.xc-dots i{width:5px;height:5px;border-radius:50%;background:var(--muted);animation:xcDot 1s infinite}
.xc-dots i:nth-child(2){animation-delay:.15s}
.xc-dots i:nth-child(3){animation-delay:.3s}
@keyframes xcDot{0%,60%,100%{opacity:.3}30%{opacity:1}}
.xc-chips{display:flex;flex-wrap:wrap;gap:6px;padding:14px 8px 10px}
.xc-chip{font-size:13px;padding:6px 12px;border-radius:999px;box-shadow:inset 0 0 0 1px var(--line);color:var(--muted)}
.xc-chip:hover{color:var(--ink)}
.xc-chip[aria-pressed="true"]{background:var(--ink);color:var(--bg);box-shadow:none}
.xc-chip-loved{display:inline-flex;align-items:center;gap:5px}
.xc-chip-loved svg{color:var(--accent)}
.xc-chip-loved[aria-pressed="true"] svg{color:inherit}
.xc-ct{font:500 11.5px/1 "Geist Mono",ui-monospace,monospace;opacity:.8}
.xc-every{display:flex;align-items:center;justify-content:center;gap:10px;margin:14px 6px 0;height:44px;border-radius:12px;border:1px dashed var(--line);font-weight:600;font-size:14px}
.xc-every:hover{background:var(--row-hover)}
.xc-every span{font:500 12px/1 "Geist Mono",ui-monospace,monospace;padding:4px 7px;border-radius:6px;background:var(--line)}
.xc-empty{padding:8px 12px;font-size:14px;color:var(--muted)}
.xc-empty a{color:var(--ink);font-weight:600;text-decoration:underline}
.xc-pane{display:flex;flex-direction:column;min-width:0;min-height:0}
.xc-pane-in{display:flex;flex-direction:column;flex:1;min-height:0;animation:xcIn .22s ease-out}
@keyframes xcIn{from{opacity:.25}to{opacity:1}}
.xc-head{position:relative;height:clamp(150px,27vh,232px);flex:none;overflow:hidden;background:var(--list)}
.xc-pic{position:absolute;inset:0}
.xc-art{position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;object-position:50% 40%;opacity:0;transition:opacity .25s ease}
.xc-art.in{opacity:1}
/* 🖼️ soft art (09-30): its own colors, blurred, across the strip, and the
   picture itself whole at the right edge, fading in from the left. A 512px
   square is drawn at most the strip's height, so it stays sharp. */
.xc-amb{position:absolute;top:-20%;left:-10%;width:120%;height:140%;object-fit:cover;filter:blur(34px) saturate(1.5) brightness(.72)}
.xc-art.xc-art-soft{left:auto;right:0;width:auto;max-width:70%;-webkit-mask-image:linear-gradient(90deg,transparent 0,#000 42%);mask-image:linear-gradient(90deg,transparent 0,#000 42%)}
/* 09-30: plus a scrim from the left, under the copy, so the title never sits on a face */
.xc-shade{position:absolute;inset:0;background:linear-gradient(0deg,var(--bg) 0%,color-mix(in srgb,var(--bg) 72%,transparent) 42%,transparent 84%),linear-gradient(90deg,color-mix(in srgb,var(--bg) 72%,transparent) 0%,color-mix(in srgb,var(--bg) 34%,transparent) 30%,transparent 58%)}
/* 🌤️ light mode (09-30, Nina: "I don't love how these look in light mode"): no
   words on the picture. It replaces 09-29's dark banner on the light page (and
   never fades dark art into white: that read as fog). */
.xc[data-mode="light"] .xc-head{background:var(--bg);box-shadow:inset 0 -1px 0 var(--line)}
.xc[data-mode="light"] .xc-head.xc-skel{background:var(--skel)}
.xc[data-mode="light"] .xc-shade{display:none}
.xc[data-mode="light"] .xc-eyebrow{color:#3B4150;background:#ECEEF2;box-shadow:inset 0 0 0 1px #E1E4EA;-webkit-backdrop-filter:none;backdrop-filter:none}
.xc[data-mode="light"] .xc-tagline{color:var(--muted);opacity:1;text-shadow:none}
/* two panes: the words on the page at the left, the picture a card at the
   right in its own shape (--ar, set by the pane), so a small one stays whole and sharp */
.xc[data-mode="light"][data-layout="wide"] .xc-head{--hh:clamp(150px,24vh,212px);--cardmax:46%;height:var(--hh)}
.xc[data-mode="light"][data-layout="wide"] .xc-pic{inset:auto;right:28px;top:18px;height:calc(100% - 36px);aspect-ratio:var(--ar,2.36);max-width:var(--cardmax);border-radius:16px;overflow:hidden;background:var(--skel);box-shadow:0 14px 30px -16px rgba(16,24,40,.5),0 0 0 1px rgba(16,24,40,.06)}
.xc[data-mode="light"][data-layout="wide"] .xc-amb{display:none}
.xc[data-mode="light"][data-layout="wide"] .xc-art.xc-art-soft{left:0;right:auto;width:100%;max-width:none;-webkit-mask-image:none;mask-image:none}
.xc[data-mode="light"][data-layout="wide"] .xc-head-copy{left:32px;top:50%;bottom:auto;transform:translateY(-50%)}
.xc[data-mode="light"][data-layout="wide"] .xc-has-pic .xc-head-copy{right:calc(min(var(--cardmax),(var(--hh) - 36px) * var(--ar,2.36)) + 60px)}
@media (max-width:1100px){.xc[data-mode="light"][data-layout="wide"] .xc-head{--cardmax:40%}.xc[data-mode="light"][data-layout="wide"] .xc-head h2{font-size:25px}}
/* a phone's thread: the picture across the top, the words under it on the page */
.xc[data-mode="light"] .xc-sheet .xc-head{height:auto;box-shadow:none}
.xc[data-mode="light"] .xc-sheet .xc-pic{position:relative;height:clamp(150px,26vh,220px);overflow:hidden}
.xc[data-mode="light"] .xc-sheet .xc-head-copy{position:static;padding:12px 18px 10px}
@media (max-height:500px){.xc[data-mode="light"] .xc-sheet .xc-pic{height:96px}}
.xc-head-copy{position:absolute;left:30px;right:30px;bottom:14px}
/* 09-30 (Nina: move "MOST VISITORS START HERE" to the right of the name and
   change its style, "it just looks off"; was 09-29's serif caps in green above
   the name): the label is a small pill beside the name, in the page's own
   type. Frosted glass over a picture; a soft gray chip on the light page. The
   row wraps: a long name (or a phone) puts the pill under it, and a name too
   long for the row alone is cut with an ellipsis. */
.xc-namerow{display:flex;flex-wrap:wrap;align-items:center;column-gap:12px;row-gap:6px;margin:0 0 5px}
.xc-eyebrow{flex:none;display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:999px;font:600 12.5px/1 Geist,ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;white-space:nowrap;color:#fff;background:rgba(255,255,255,.16);box-shadow:inset 0 0 0 1px rgba(255,255,255,.24);-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px)}
.xc-head h2{font-size:30px;font-weight:700;letter-spacing:-.02em;line-height:1.1;margin:0;min-width:0;flex:0 1 auto;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
/* the tagline on its own line (sweep #162) keeps the 09-29 legibility: ink over the art with a soft shadow */
.xc-tagline{font-size:15px;line-height:1.38;color:var(--ink);opacity:.9;margin:0 0 8px;max-width:720px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:anywhere;text-shadow:0 1px 3px rgba(0,0,0,.6)}
.xc-head-meta{display:flex;align-items:center;gap:10px;color:var(--muted);font-size:13.5px;min-width:0}
.xc-visits{flex:none;white-space:nowrap}
.xc-fc{display:inline-flex;align-items:center;flex:none}
.xc-fc img{width:24px;height:24px;border-radius:50%;object-fit:cover;border:2px solid var(--bg);margin-left:-7px;background:var(--row-hover)}
.xc-fc img:first-child{margin-left:0}
.xc-thread{flex:1;min-height:0;overflow:hidden;display:flex;flex-direction:column;justify-content:flex-end;gap:10px;padding:6px 30px 14px;-webkit-mask-image:linear-gradient(to bottom,transparent 0,#000 20%);mask-image:linear-gradient(to bottom,transparent 0,#000 20%)}
.xc-msg{display:flex;gap:10px;align-items:flex-end;max-width:min(72%,660px)}
.xc-msg.r{align-self:flex-end;flex-direction:row-reverse}
.xc-av1{flex:none;width:28px;height:28px;border-radius:50%;display:grid;place-items:center;overflow:hidden;font-size:12px;font-weight:700;background:hsl(var(--h) var(--bub-s) calc(var(--bub-l) + 10%));color:hsl(var(--h) var(--name-s) var(--name-l))}
.xc-av1 img{width:100%;height:100%;object-fit:cover}
.xc-bub{min-width:0;background:hsl(var(--h) var(--bub-s) var(--bub-l));color:var(--bub-ink);border-radius:16px 16px 16px 5px;font-size:14px;line-height:1.4;padding:8px 13px;overflow-wrap:anywhere}
.xc-msg.r .xc-bub{border-radius:16px 16px 5px 16px}
.xc-bub b{display:block;font-size:11.5px;font-weight:700;margin-bottom:1px;color:hsl(var(--h) var(--name-s) var(--name-l))}
.xc-bub-media{padding:8px}
.xc-bub-media b{padding:0 5px}
.xc-bub-media span{display:block;padding:0 5px 4px}
.xc-gif{display:block;width:auto;height:auto;max-width:160px;max-height:120px;border-radius:10px;margin-top:4px;background:var(--row-hover)}
.xc-enter{animation:xcBub .34s cubic-bezier(.2,.8,.2,1)}
.xc-leave{opacity:0!important;transform:translateY(-5px);transition:opacity .28s,transform .28s}
@keyframes xcBub{from{opacity:0;transform:translateY(8px) scale(.985)}to{opacity:1;transform:none}}
.xc-quiet{align-self:center;margin-bottom:18px;color:var(--muted);font-size:14px}
.xc-composer{flex:none;display:flex;align-items:center;gap:10px;margin:0 30px 22px;padding:7px 7px 7px 18px;border-radius:14px;background:var(--list);box-shadow:0 0 0 1px var(--line);cursor:pointer}
.xc-composer:hover{box-shadow:0 0 0 1px var(--muted)}
.xc-composer-off{cursor:default}
.xc-input{flex:1;min-width:0;color:var(--muted);font-size:14.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:40px}
.xc-input b{color:var(--ink);font-weight:600}
.xc-send{flex:none;display:inline-flex;align-items:center;gap:4px;height:40px;padding:0 12px 0 16px;border-radius:10px;background:var(--accent);color:var(--accent-ink);font-weight:700;font-size:14.5px;white-space:nowrap}
.xc-composer:hover .xc-send{filter:brightness(1.08)}
/* 💬 Send: a real message box, with Go in beside it */
.xc-composer-send{cursor:default;gap:8px;padding:6px 6px 6px 16px}
.xc-composer-send:hover{box-shadow:0 0 0 1px var(--line)}
.xc-composer-send:focus-within{box-shadow:0 0 0 2px var(--focus)}
.xc-composer-send:hover .xc-send{filter:none}
.xc-composer-send .xc-send:hover{filter:brightness(1.08)}
.xc-field{flex:1;min-width:0;height:40px;border:0;outline:0;background:none;color:var(--ink);font:inherit;font-size:14.5px;padding:0;text-overflow:ellipsis}
.xc-field::placeholder{color:var(--muted);opacity:1}
.xc-field:disabled{cursor:default}
.xc-sendbtn{flex:none;display:grid;place-items:center;width:40px;height:40px;border-radius:10px;background:var(--accent);color:var(--accent-ink)}
.xc-sendbtn:disabled{background:var(--row-hover);color:var(--muted);cursor:default}
.xc-send-alt{background:none;color:var(--ink);box-shadow:inset 0 0 0 1px var(--line)}
.xc-mine .xc-bub{background:color-mix(in srgb,var(--accent) 24%,var(--list))}
.xc-note{flex:none;margin:0 32px 8px;font-size:13px;color:var(--muted)}
.xc-fail{margin:auto;text-align:center;display:flex;flex-direction:column;align-items:center;gap:14px;color:var(--muted)}
.xc-skel{background:var(--skel);border-radius:8px;animation:xcSkel 1.4s ease-in-out infinite}
.xc-skel-row:hover{background:none}
.xc-skel-av{display:block;width:44px;height:44px;margin-left:4px;border-radius:50%}
.xc-skel-a{display:block;width:60%;height:13px}
.xc-skel-b{display:block;width:85%;height:11px;margin-top:8px}
@keyframes xcSkel{50%{opacity:.55}}
/* 📱 one column: a phone, a phone on its side, a narrow window. The page
   scrolls (not a fixed box), the search sticks, the header clears the music
   button (34px at 10/10 under 768px wide, 48px at 20/20 above). */
.xc[data-layout="narrow"]{height:auto;min-height:100vh;min-height:100dvh;display:block;background:var(--list)}
.xc[data-layout="narrow"] .xc-top{height:auto;min-height:60px;padding:calc(8px + env(safe-area-inset-top)) 56px 6px 16px;gap:8px;border-bottom:0}
@media (min-width:768px){.xc[data-layout="narrow"] .xc-top{padding-right:92px}}
.xc[data-layout="narrow"] .xc-wm{flex-direction:column;align-items:flex-start;gap:4px}
.xc[data-layout="narrow"] .xc-wm-n{font-size:21px}
.xc[data-layout="narrow"] .xc-wm-t{font-size:11.5px;letter-spacing:.06em}
.xc[data-layout="narrow"] .xc-new-pill{padding:0 8px}
.xc[data-layout="narrow"] .xc-new [role="dialog"]{position:fixed!important;top:calc(64px + env(safe-area-inset-top))!important;left:10px!important;right:10px!important;width:auto!important;max-height:calc(100vh - 84px)!important;max-height:calc(100dvh - 84px)!important}
.xc[data-layout="narrow"] .xc-app{display:block}
.xc[data-layout="narrow"] .xc-list{overflow:visible;border-right:0;padding:0 8px calc(26px + env(safe-area-inset-bottom))}
.xc[data-layout="narrow"] .xc-find{position:sticky;top:0;z-index:15;display:flex;align-items:center;gap:8px;margin:0 -8px 6px;padding:8px 56px 10px 14px;background:var(--list)}
@media (min-width:768px){.xc[data-layout="narrow"] .xc-find{padding-right:92px}}
@media (max-width:359px){.xc-disc span{display:none}.xc-disc{width:42px;padding:0;justify-content:center}}
.xc[data-layout="narrow"] .xc-find.stuck{box-shadow:0 1px 0 var(--line)}
.xc[data-layout="narrow"] .xc-search{flex:1;min-width:0;height:42px;margin:0}
.xc[data-layout="narrow"] .xc-search input{font-size:16px}
/* 📱 the sticky bar: [search] [Discover] [+]. Discover is an outline with
   cards and a label; Create is a plain outlined + (09-30: no green, as on
   desktop), still a full 42px to tap. */
.xc-compose{flex:none;display:grid;place-items:center;width:42px;height:42px;border-radius:10px;color:var(--ink);box-shadow:inset 0 0 0 1px var(--line)}
.xc-compose:active{background:var(--row-hover)}
.xc-disc{flex:none;display:inline-flex;align-items:center;gap:6px;height:42px;padding:0 12px 0 10px;border-radius:10px;box-shadow:inset 0 0 0 1px var(--line);font-weight:600;font-size:14px;white-space:nowrap}
.xc-disc svg{color:rgb(var(--live))}
.xc-disc:active{background:var(--row-hover)}
.xc[data-layout="narrow"] .xc-row{padding:10px}
.xc[data-layout="narrow"] .xc-rn{font-size:15px}
.xc[data-layout="narrow"] .xc-rl{font-size:13.5px;height:20px}
.xc[data-layout="narrow"] .xc-every{margin:16px 4px 0;height:48px}
.xc-foot{display:flex;justify-content:center;padding:14px 0 0}
.xc-byai-foot{display:inline-flex;align-items:center;gap:7px;padding:10px 14px;font-size:14px;font-weight:600;color:var(--muted)}
.xc-legal{display:flex;align-items:center;justify-content:center;gap:6px;padding:12px 0 2px;font-size:12px;line-height:1.2;color:var(--muted);opacity:.85}
.xc-legal a{padding:4px 2px}
.xc-legal a:hover{color:var(--ink);text-decoration:underline}
.xc-fail-list{padding:40px 0}
/* 📱 the thread: one room full screen, pushed in from the right like a chat */
.xc-sheet{position:fixed;inset:0;z-index:4000;display:flex;flex-direction:column;background:var(--bg);overscroll-behavior:contain;touch-action:pinch-zoom;animation:xcPush .28s cubic-bezier(.2,.8,.2,1)}
.xc-sheet-out{animation:xcPop .19s ease-in forwards}
@keyframes xcPush{from{transform:translateX(100%)}to{transform:none}}
@keyframes xcPop{from{transform:none}to{transform:translateX(100%)}}
.xc-sheet .xc-pane-in{animation:none}
.xc-sheet .xc-head{height:clamp(140px,31vh,270px);height:clamp(140px,31dvh,270px)}
.xc-sheet .xc-head-copy{left:18px;right:18px;bottom:12px}
.xc-sheet .xc-head h2{font-size:25px}
.xc-sheet .xc-namerow{margin-bottom:4px}
.xc-sheet .xc-tagline{font-size:14px;margin-bottom:7px}
.xc-sheet .xc-head-meta{font-size:13px;gap:10px}
/* a short phone upright (Safari's bars leave an iPhone SE about 548px): the
   faces row steps out, so a two-line tagline keeps the title clear of the ‹ */
@media (max-height:620px){.xc-sheet .xc-head-meta{display:none}.xc-sheet .xc-tagline{margin-bottom:0}}
/* a phone on its side: the head is only 140px, so the tagline takes one line
   and the faces step out (the title stays clear of the back button) */
@media (max-height:500px){.xc-sheet .xc-tagline{-webkit-line-clamp:1;margin-bottom:0}.xc-sheet .xc-head-meta{display:none}}
.xc-sheet .xc-thread{padding:6px 14px 12px}
.xc-sheet .xc-msg{max-width:min(88%,600px)}
.xc-sheet .xc-composer{margin:0 12px 14px;margin-bottom:max(14px,env(safe-area-inset-bottom))}
.xc-sheet .xc-input{font-size:15px}
.xc-sheet .xc-note{margin:0 16px 8px}
.xc[data-layout="narrow"] .xc-field{font-size:16px}
.xc-back{position:absolute;z-index:5;top:calc(10px + env(safe-area-inset-top));left:12px;width:40px;height:40px;border-radius:50%;display:grid;place-items:center;background:rgba(13,15,19,.62);color:#fff;box-shadow:inset 0 0 0 1px rgba(255,255,255,.22)}
@media (prefers-reduced-motion:reduce){.xc-enter,.xc-pane-in,.xc-skel,.xc-here,.xc-live-dot,.xc-sheet,.xc-sheet-out{animation:none}.xc-leave,.xc-art{transition:none}.xc-dots i{animation:none;opacity:.6}}
`;
